<admin-breadcrumbs></admin-breadcrumbs>

<h2 class="sr-only">Internal decline reasons administration</h2>

<div class="idr-shell">
  <div class="idr-page-inner">
      <div class="idr-page-header">
        <div class="idr-page-title">
          Internal decline reasons
          <app-ui-badge variant="info">New</app-ui-badge>
        </div>
      </div>

      <div class="idr-feature-panel" [class.idr-feature-panel--on]="featureEnabled">
        <div class="idr-feature-panel-icon" [class.idr-feature-panel-icon--on]="featureEnabled">
          <i class="fa fa-shield-alt" aria-hidden="true"></i>
        </div>
        <div class="idr-feature-panel-copy">
          <span class="idr-feature-panel-label">Visibility to internal team</span>
          <p class="idr-feature-panel-sub">
            When off, the reason picker and pill are hidden from declined referrals for everyone in this region.
          </p>
          <p *ngIf="featureToggleError" class="idr-feedback idr-feedback--error" role="alert">{{ featureToggleError }}</p>
        </div>
        <app-ui-toggle
          [checked]="featureEnabled"
          [disabled]="featureSaving"
          ariaLabel="Toggle internal decline reasons"
          (toggled)="toggleFeature()"
        ></app-ui-toggle>
      </div>

      <p class="idr-page-sub">
        Manage the closed list of internal decline reasons available to your team, grouped by category. These
        reasons are visible to internal users only — they are <strong>not</strong> shared with the sending
        hospital. Every new category starts with one reason of the same name, so it can be picked right away.
        Deleting a category also retires the reasons filed under it, and deleting a category's last reason
        deletes that category too; cases already using a reason keep it — rename instead if you want the
        wording changed on those cases.
      </p>

      <div class="idr-state" *ngIf="loading">
        <app-ui-spinner label="Loading reasons"></app-ui-spinner>
        <span>Loading reasons…</span>
      </div>
      <div class="idr-state idr-state--error" *ngIf="loadError" role="alert">{{ loadError }}</div>

      <div *ngIf="!loading && !loadError">
        <div class="idr-feedback idr-feedback--error" *ngIf="actionError" role="alert">{{ actionError }}</div>
        <div class="idr-feedback idr-feedback--success" *ngIf="actionMessage" role="status">{{ actionMessage }}</div>
        <div class="idr-feedback idr-feedback--warning" *ngIf="refreshError" role="alert">{{ refreshError }}</div>

        <app-ui-card padding="0">
          <div class="idr-table-toolbar">
            <app-ui-button variant="primary" (pressed)="startAddCategory()">+ Add category</app-ui-button>
          </div>

          <div class="idr-tree" aria-label="Internal decline reason categories">
            <ng-container *ngFor="let group of categoryGroups; trackBy: trackByCategoryId">
              <div class="idr-category-row">
                <button
                  type="button"
                  class="idr-category-toggle"
                  [attr.aria-expanded]="group.expanded"
                  [attr.aria-label]="(group.expanded ? 'Collapse ' : 'Expand ') + group.category.name"
                  (click)="toggleCategory(group.category.reasonId)"
                >
                  <i class="fa" [class.fa-chevron-down]="group.expanded" [class.fa-chevron-right]="!group.expanded"></i>
                </button>

                <div class="idr-category-main">
                  <ng-container *ngIf="editingReasonId === group.category.reasonId; else categoryNameReadonly">
                    <app-ui-input
                      [(ngModel)]="editDraftName"
                      [disabled]="renamingReasonId === group.category.reasonId"
                      ariaLabel="Rename category"
                      (keydown.enter)="saveRename(group.category)"
                      (keydown.escape)="cancelRename()"
                    ></app-ui-input>
                  </ng-container>
                  <ng-template #categoryNameReadonly>
                    <span class="idr-category-name">{{ group.category.name }}</span>
                    <span class="idr-reason-count">{{ group.reasons.length }} reason{{ group.reasons.length === 1 ? '' : 's' }}</span>
                  </ng-template>
                </div>

                <div class="idr-row-actions">
                  <ng-container *ngIf="editingReasonId === group.category.reasonId; else categoryDefaultActions">
                    <app-ui-button
                      variant="ghost"
                      [disabled]="renamingReasonId === group.category.reasonId"
                      (pressed)="cancelRename()"
                    >
                      Cancel
                    </app-ui-button>
                    <app-ui-button
                      variant="primary"
                      [loading]="renamingReasonId === group.category.reasonId"
                      (pressed)="saveRename(group.category)"
                    >
                      Save
                    </app-ui-button>
                  </ng-container>
                  <ng-template #categoryDefaultActions>
                    <app-ui-icon-button
                      icon="fa-plus"
                      [ariaLabel]="'Add reason to ' + group.category.name"
                      [disabled]="deleteTargetReasonId === group.category.reasonId"
                      (pressed)="startAddReasonToCategory(group.category.reasonId)"
                    ></app-ui-icon-button>
                    <app-ui-icon-button
                      icon="fa-pen"
                      [ariaLabel]="'Rename ' + group.category.name"
                      [disabled]="deleteTargetReasonId === group.category.reasonId"
                      (pressed)="startRename(group.category)"
                    ></app-ui-icon-button>
                    <app-ui-icon-button
                      icon="fa-trash"
                      variant="ghost-danger"
                      [ariaLabel]="'Delete ' + group.category.name"
                      [disabled]="deleteTargetReasonId === group.category.reasonId"
                      (pressed)="openDeleteDialog(group.category)"
                    ></app-ui-icon-button>
                  </ng-template>
                </div>
              </div>

              <div class="idr-confirm-row" *ngIf="deleteTargetReasonId === group.category.reasonId">
                <app-ui-confirm
                  [message]="deleteConfirmMessage"
                  confirmLabel="Delete"
                  [loading]="deletingReasonId === group.category.reasonId"
                  [disabled]="deletingReasonId !== null"
                  (confirmed)="confirmDelete()"
                  (cancelled)="closeDeleteDialog()"
                >
                  <app-ui-button
                    *ngIf="deleteTargetInUse"
                    variant="ghost"
                    [disabled]="deletingReasonId !== null"
                    (pressed)="renameInstead()"
                  >
                    Rename instead
                  </app-ui-button>
                </app-ui-confirm>
              </div>

              <div class="idr-reason-list" *ngIf="group.expanded">
                <ng-container *ngFor="let reason of group.reasons; trackBy: trackByReasonId">
                  <div class="idr-reason-row">
                    <div class="idr-reason-main">
                      <ng-container *ngIf="editingReasonId === reason.reasonId; else reasonNameReadonly">
                        <app-ui-input
                          [(ngModel)]="editDraftName"
                          [disabled]="renamingReasonId === reason.reasonId"
                          ariaLabel="Rename internal decline reason"
                          (keydown.enter)="saveRename(reason)"
                          (keydown.escape)="cancelRename()"
                        ></app-ui-input>
                      </ng-container>
                      <ng-template #reasonNameReadonly>
                        <span class="idr-reason-name">{{ reason.name }}</span>
                      </ng-template>
                    </div>

                    <span class="idr-use-count" [class.idr-use-count--zero]="reason.usageCount === 0">
                      {{ useCountLabel(reason.usageCount) }}
                    </span>

                    <div class="idr-row-actions">
                      <ng-container *ngIf="editingReasonId === reason.reasonId; else reasonDefaultActions">
                        <app-ui-button
                          variant="ghost"
                          [disabled]="renamingReasonId === reason.reasonId"
                          (pressed)="cancelRename()"
                        >
                          Cancel
                        </app-ui-button>
                        <app-ui-button
                          variant="primary"
                          [loading]="renamingReasonId === reason.reasonId"
                          (pressed)="saveRename(reason)"
                        >
                          Save
                        </app-ui-button>
                      </ng-container>
                      <ng-template #reasonDefaultActions>
                        <app-ui-icon-button
                          icon="fa-pen"
                          ariaLabel="Rename reason"
                          [disabled]="deleteTargetReasonId === reason.reasonId"
                          (pressed)="startRename(reason)"
                        ></app-ui-icon-button>
                        <app-ui-icon-button
                          icon="fa-trash"
                          variant="ghost-danger"
                          ariaLabel="Delete reason"
                          [disabled]="deleteTargetReasonId === reason.reasonId"
                          (pressed)="openDeleteDialog(reason)"
                        ></app-ui-icon-button>
                      </ng-template>
                    </div>
                  </div>

                  <div class="idr-confirm-row" *ngIf="deleteTargetReasonId === reason.reasonId">
                    <app-ui-confirm
                      [message]="deleteConfirmMessage"
                      confirmLabel="Delete"
                      [loading]="deletingReasonId === reason.reasonId"
                      [disabled]="deletingReasonId !== null"
                      (confirmed)="confirmDelete()"
                      (cancelled)="closeDeleteDialog()"
                    >
                      <app-ui-button
                        *ngIf="deleteTargetInUse"
                        variant="ghost"
                        [disabled]="deletingReasonId !== null"
                        (pressed)="renameInstead()"
                      >
                        Rename instead
                      </app-ui-button>
                    </app-ui-confirm>
                  </div>
                </ng-container>

                <div class="idr-add-row idr-add-row--nested" *ngIf="addingReasonToCategoryId === group.category.reasonId">
                  <app-ui-input
                    #newReasonInput
                    class="idr-add-row__input"
                    [(ngModel)]="newReasonName"
                    [disabled]="creating"
                    placeholder="e.g. Patient declined our facility"
                    ariaLabel="New reason name"
                    (keydown.enter)="createReasonInCategory(group.category.reasonId)"
                    (keydown.escape)="cancelAddReason()"
                  ></app-ui-input>
                  <app-ui-button variant="ghost" [disabled]="creating" (pressed)="cancelAddReason()">Cancel</app-ui-button>
                  <app-ui-button
                    variant="primary"
                    [loading]="creating"
                    (pressed)="createReasonInCategory(group.category.reasonId)"
                  >
                    Add reason
                  </app-ui-button>
                </div>

                <p class="idr-empty idr-empty--nested" *ngIf="group.reasons.length === 0 && addingReasonToCategoryId !== group.category.reasonId">
                  No reasons in this category yet.
                </p>
              </div>
            </ng-container>
          </div>

          <div class="idr-add-row" *ngIf="addingCategory">
            <app-ui-input
              #newCategoryInput
              class="idr-add-row__input"
              [(ngModel)]="newCategoryName"
              [disabled]="creating"
              placeholder="e.g. Clinical Fit"
              ariaLabel="New category name"
              (keydown.enter)="createCategory()"
              (keydown.escape)="cancelAddCategory()"
            ></app-ui-input>
            <app-ui-button variant="ghost" [disabled]="creating" (pressed)="cancelAddCategory()">Cancel</app-ui-button>
            <app-ui-button variant="primary" [loading]="creating" (pressed)="createCategory()">Add category</app-ui-button>
          </div>

          <p class="idr-empty" *ngIf="categoryGroups.length === 0 && !addingCategory">
            No categories yet. Click “+ Add category” to create the first one.
          </p>
        </app-ui-card>
      </div>
    </div>
</div>
